import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { ToggleField } from '@atlassian/company-hub-common';

import { AUTOPLAY_SECONDS, carouselConfigAnalyticSource } from '../../../../constants';
import type { UpdateMainFormFieldType } from '../../../../controllers/use-carousel-extension-parameters-and-actions/types';
import type { CarouselParameters } from '../../../../types';

import { messages } from './messages';

type AutoplayToggleProps = {
	isAutoplayEnabled: CarouselParameters['isAutoplayEnabled'];
	updateMainFormField: UpdateMainFormFieldType;
};

export const AutoplayToggle = ({
	isAutoplayEnabled,
	updateMainFormField,
}: AutoplayToggleProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleChange = useCallback(() => {
		updateMainFormField({ isAutoplayEnabled: !isAutoplayEnabled });
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'toggle',
				actionSubjectId: 'autoplayToggle',
				source: carouselConfigAnalyticSource,
			},
		}).fire();
	}, [createAnalyticsEvent, isAutoplayEnabled, updateMainFormField]);

	return (
		<ToggleField
			label={formatMessage(messages.autoplayLabel)}
			Icon={
				<Tooltip
					content={formatMessage(messages.autoplayTooltipContent, { number: AUTOPLAY_SECONDS })}
					position="bottom"
				>
					<StatusInformationIcon
						testId="autoplay-information-icon"
						label="autoplay-information"
						color={token('color.icon.discovery')}
					/>
				</Tooltip>
			}
			isChecked={isAutoplayEnabled}
			onChange={handleChange}
		/>
	);
};
